<script setup>
import { computed, ref } from 'vue';
import { useStore } from 'vuex';

const store = useStore();

const LANGUAGES = [
  { value: 'fr', text: '🇫🇷 Français' },
  { value: 'en', text: '🇺🇸 English' },
];

const popupResolve = computed(() => store.getters['popupResolve']);
const popupReject = computed(() => store.getters['popupReject']);
const popupData = computed(() => store.getters['popupData']);

const canStartTranslating = computed(() => originLanguage.value && targetLanguage.value);

const originLanguage = ref('');
const targetLanguage = ref('');

const clearTargetLanguage = () => {
  if (originLanguage.value === targetLanguage.value) targetLanguage.value = '';
};

const startTranslation = async () => {
  try {
    const result = await store.dispatch('resumes.translate', {
      id: popupData.value.id,
      source: originLanguage.value,
      target: targetLanguage.value,
      isOrganization: popupData.value.workspaceType === 1,
    });

    popupResolve.value(result);
  } catch (e) {
    popupReject.value(e);
  }
};
</script>

<template>
  <div class="header">
    <span class="title ellipsis">{{ $t('popUp.translation.title') }}</span>
  </div>
  <div class="translate-popup-body">
    <span class="translate-popup-body-title">{{ $t('popUp.translation.generalInfo') }}</span>
    <div class="lang-form">
      <label for="originLanguage">{{ $t('popUp.translation.originLanguage') }}</label>
      <select id="originLanguage" v-model="originLanguage" name="originLanguage" @change="clearTargetLanguage">
        <option value="" disabled selected hidden>{{ $t('popUp.translation.langChoice') }}</option>
        <option v-for="lang in LANGUAGES" :key="lang.value" :value="lang.value">{{ lang.text }}</option>
      </select>
    </div>
    <div class="lang-form">
      <label for="targetLanguage">{{ $t('popUp.translation.targetLanguage') }}</label>
      <select id="targetLanguage" v-model="targetLanguage" name="targetLanguage">
        <option value="" disabled selected hidden>{{ $t('popUp.translation.langChoice') }}</option>
        <option
          v-for="lang in LANGUAGES.filter(item => item.value !== originLanguage)"
          :key="lang.value"
          :value="lang.value"
        >
          {{ lang.text }}
        </option>
      </select>
      <span>{{ $t('popUp.translation.details') }}</span>
    </div>
  </div>
  <div class="translate-popup-buttons">
    <button class="button-cancel" @click="popupResolve">{{ $t('popUp.translation.cancel') }}</button>
    <button class="button-create" :class="{ disabled: !canStartTranslating }" @click="startTranslation">
      {{ $t('popUp.translation.translate') }}
    </button>
  </div>
</template>

<style scoped lang="scss">
@use '@/styles/colors.scss';

.header {
  width: 100%;
  border-bottom: 1px solid colors.$light-blue-color;
  padding-bottom: 22px;
  .title {
    color: colors.$light-blue-color;
    font-size: 16px;
    font-weight: bold;
    text-transform: uppercase;
  }
}

.translate-popup-body {
  display: flex;
  flex-direction: column;
  padding: 2rem 0;
  gap: 1rem;

  .translate-popup-body-title {
    font-weight: 700;
    font-family: 'Lato', sans-serif;
    font-size: 1.1rem;
  }

  .lang-form {
    display: flex;
    flex-direction: column;
    gap: 5px;

    > label {
      color: #939292;
      font-size: 1rem;
      line-height: 1.1rem;
    }

    > select {
      background-color: #ffffff;
      border: 1px solid #979797;
      border-radius: 3px;
      padding: 4px;
    }

    > span {
      font-family: 'Lato', sans-serif;
      font-weight: 400;
      font-style: italic;
      color: colors.$light-blue-color;
      font-size: 0.7rem;
      line-height: 0.8rem;
    }
  }
}

.translate-popup-buttons {
  display: flex;
  justify-content: center;
  gap: 1.3rem;

  > button {
    cursor: pointer;
    text-transform: uppercase;
  }
}
</style>
